import type { ColumnDef, Row } from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import type { CSSProperties, ReactNode } from 'react';
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { TableVirtuoso, type TableVirtuosoHandle } from 'react-virtuoso';
import { cn } from '@/shared/lib/utils';
import type { ExpandableConfig, PinnedColumnInfo, VirtualScrollConfig } from '../helpers/types';
import {
  getHighlightedPinnedClassName,
  getPinnedClassName,
  mergeColumnStyles,
} from '../helpers/utils';
import { TableCell } from './TableCell';
import { TableRow } from './TableRow';

const DEFAULT_OVERSCAN = 10;

/**
 * 仮想アイテムの種別
 * - data: データ行（セル表示）
 * - expanded: 展開行（展開コンテンツ表示）
 */
type VirtualItem = { type: 'data'; rowIndex: number } | { type: 'expanded'; rowIndex: number };

type VirtualTableBodyProps<TData, TValue> = {
  rows: Row<TData>[];
  columns: ColumnDef<TData, TValue>[];
  columnStyles: Record<string, CSSProperties | undefined>;
  weightedLeafWidthStyles: Record<string, CSSProperties>;
  pinnedColumnStyles?: Record<string, PinnedColumnInfo>;
  showLastBorderBottom: boolean;
  expandable?: ExpandableConfig<TData>;
  highlightedRowIds?: string[];
  selectedRowIds?: string[];
  virtualScrollConfig: VirtualScrollConfig;
  scrollToRowId?: string | null;
  /** テーブルの最小幅（カラム幅固定時） */
  tableMinWidth?: number;
  /** ヘッダー行のレンダリング関数（固定ヘッダー） */
  renderHeaderContent: () => ReactNode;
  /** カラムグループのレンダリング関数 */
  renderColgroup: () => ReactNode;
};

// border-separate 用: Virtuoso テーブル直下の td/th にのみスタイルを付与。
// 子孫セレクタ [&_td] だと展開コンテンツ内のネストテーブルにも適用されてしまうため、
// 直接の子要素パスで限定する。
// - border-b: border-separate では tr の border-b が効かないため td/th に直接付与
// - bg-surface-base: border-separate では td に背景色がないと親要素の色が透過する
const BORDER_SEPARATE_CELL_CLASS = [
  '[&>thead>tr>th]:border-b [&>thead>tr>th]:border-border-strength1',
  '[&>tbody>tr>td]:border-b [&>tbody>tr>td]:border-border-strength1 [&>tbody>tr>td]:bg-surface-base',
].join(' ');

// Virtuoso の components に渡すコンポーネントをモジュールスコープで定義。
// render 内でインライン定義すると毎レンダリングで新しいコンポーネント型が生まれ、
// Virtuoso がサブツリー全体を再マウントしてしまう。
const VirtuosoTable = forwardRef<HTMLTableElement, React.ComponentProps<'table'>>(
  ({ style, children, ...props }, ref) => (
    <table
      ref={ref}
      className={cn(
        'w-full caption-bottom border-separate border-spacing-0 text-sm',
        BORDER_SEPARATE_CELL_CLASS,
      )}
      style={style}
      {...props}
    >
      {children}
    </table>
  ),
);

VirtuosoTable.displayName = 'VirtuosoTable';

const VirtuosoTableHead = forwardRef<HTMLTableSectionElement, React.ComponentProps<'thead'>>(
  ({ style, ...props }, ref) => (
    <thead ref={ref} className="bg-surface-strength2" style={style} {...props} />
  ),
);

VirtuosoTableHead.displayName = 'VirtuosoTableHead';

const VirtuosoTableBody = forwardRef<HTMLTableSectionElement, React.ComponentProps<'tbody'>>(
  (props, ref) => <tbody ref={ref} className="bg-surface-base" {...props} />,
);

VirtuosoTableBody.displayName = 'VirtuosoTableBody';

/**
 * React Virtuoso (TableVirtuoso) ベースの仮想スクロールテーブルボディ
 *
 * border-separate + border-spacing-0 を使用し、各セル（td/th）に border-b を付与することで
 * Chrome の border-collapse + position: sticky でボーダーが消えるバグを回避する。
 *
 * 展開行をデータ行とは別の仮想アイテムとして扱うことで、
 * Virtuoso の高さ計測が正確に動作し、スクロール位置のズレを防ぐ。
 */
export const VirtualTableBody = <TData, TValue>({
  rows,
  columns,
  columnStyles,
  weightedLeafWidthStyles,
  pinnedColumnStyles,
  showLastBorderBottom,
  expandable,
  highlightedRowIds,
  selectedRowIds,
  virtualScrollConfig,
  scrollToRowId,
  tableMinWidth,
  renderHeaderContent,
  renderColgroup,
}: VirtualTableBodyProps<TData, TValue>) => {
  const virtuosoRef = useRef<TableVirtuosoHandle>(null);
  const overscan = virtualScrollConfig.overscan ?? DEFAULT_OVERSCAN;

  // expandable から使用する関数を個別に取り出し、useMemo の依存を安定化する
  const rowExpandable = expandable?.rowExpandable;
  const expandedRowRender = expandable?.expandedRowRender;

  // データ行 + 展開行を個別の仮想アイテムにフラット化
  const virtualItems = useMemo(() => {
    const items: VirtualItem[] = [];
    for (let i = 0; i < rows.length; i++) {
      items.push({ type: 'data', rowIndex: i });
      const row = rows[i];
      if (!row) continue;
      const canExpand = rowExpandable?.(row) ?? row.getCanExpand();
      const isExpanded = row.getIsExpanded();
      if (canExpand && isExpanded && expandedRowRender?.(row)) {
        items.push({ type: 'expanded', rowIndex: i });
      }
    }
    return items;
  }, [rows, rowExpandable, expandedRowRender]);

  // scrollToRowId → スクロール処理
  // 対象行が既にDOM上にレンダリング済みの場合はスムーズスクロール、
  // 未レンダリングの場合は Virtuoso の scrollToIndex でジャンプする
  const lastScrolledKeyRef = useRef<string | null>(null);
  const scrollerRef = useRef<HTMLElement | Window | null>(null);
  useEffect(() => {
    const scrollKey = scrollToRowId ? `${scrollToRowId}:${virtualItems.length}` : null;
    if (!scrollToRowId || !scrollKey) {
      lastScrolledKeyRef.current = null;
      return;
    }
    if (lastScrolledKeyRef.current === scrollKey) return;

    const targetVirtualIndex = virtualItems.findIndex(
      (item) => item.type === 'data' && rows[item.rowIndex]?.id === scrollToRowId,
    );
    if (targetVirtualIndex >= 0) {
      // Virtuoso の初回レンダリング完了を待つために遅延実行。
      // requestAnimationFrame だと Virtuoso の内部レイアウト計算が未完了の場合があるため、
      // 200ms の固定遅延で安全マージンを取っている。
      const timerId = setTimeout(() => {
        const scroller = scrollerRef.current;
        const container = scroller instanceof HTMLElement ? scroller : null;
        const targetRow = container?.querySelector(`[data-row-id="${scrollToRowId}"]`);

        if (targetRow && container) {
          // 対象行が既にレンダリング済み（隣接アイテムへのページ送り等）
          // → Virtuoso のスクロールコンテナ内でスムーズスクロール（ヘッダー直下に配置）
          const rowRect = targetRow.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          const thead = container.querySelector('thead');
          const headerHeight = thead?.getBoundingClientRect().height ?? 0;
          const offsetInContainer =
            rowRect.top - containerRect.top + container.scrollTop - headerHeight;
          container.scrollTo({ top: offsetInContainer, behavior: 'smooth' });
        } else {
          // 対象行が未レンダリング（初回ロード等）→ Virtuoso の scrollToIndex でジャンプ
          virtuosoRef.current?.scrollToIndex({
            index: targetVirtualIndex,
            align: 'start',
          });
        }
        lastScrolledKeyRef.current = scrollKey;
      }, 200);
      return () => clearTimeout(timerId);
    }
  }, [scrollToRowId, virtualItems, rows]);

  const heightStyle =
    typeof virtualScrollConfig.height === 'number'
      ? `${virtualScrollConfig.height}px`
      : virtualScrollConfig.height;

  const tableStyle: CSSProperties = useMemo(
    () => (tableMinWidth ? { minWidth: `${tableMinWidth}px` } : {}),
    [tableMinWidth],
  );

  // renderColgroup / tableStyle を ref 経由で参照し、components の参照を完全に安定化する。
  // components オブジェクトが変わると Virtuoso がサブツリーを再マウントするため、
  // 関数参照の不安定さに依存させない。
  const renderColgroupRef = useRef(renderColgroup);
  renderColgroupRef.current = renderColgroup;
  const tableStyleRef = useRef(tableStyle);
  tableStyleRef.current = tableStyle;

  const [components] = useState(() => ({
    Table: forwardRef<HTMLTableElement, React.ComponentProps<'table'>>(
      ({ style: virtuosoStyle, children, ...props }, ref) => (
        <VirtuosoTable ref={ref} style={{ ...tableStyleRef.current, ...virtuosoStyle }} {...props}>
          {renderColgroupRef.current()}
          {children}
        </VirtuosoTable>
      ),
    ),
    TableHead: VirtuosoTableHead,
    TableBody: VirtuosoTableBody,
  }));

  return (
    <TableVirtuoso
      ref={virtuosoRef}
      scrollerRef={(ref) => {
        scrollerRef.current = ref;
      }}
      totalCount={virtualItems.length}
      overscan={overscan}
      style={{ height: heightStyle }}
      components={{
        ...components,
        // TableRow はクロージャ変数（virtualItems, rows, highlightedRowIds）に依存するため、
        // モジュールスコープに出せない。Virtuoso は TableRow の参照変更時に再マウントせず
        // 既存DOMに対して再レンダリングするため、パフォーマンスへの影響は限定的。
        TableRow: ({ children, ...props }) => {
          const virtualIndex = props['data-index'] as number;
          const item = virtualItems[virtualIndex];
          if (!item) return <tr {...props}>{children}</tr>;

          const row = rows[item.rowIndex];
          if (!row) return <tr {...props}>{children}</tr>;

          if (item.type === 'expanded') {
            return (
              <tr {...props} className="[&>td]:bg-surface-base">
                {children}
              </tr>
            );
          }

          const isLastDataRow = item.rowIndex === rows.length - 1;
          const isHighlighted = highlightedRowIds?.includes(row.id);
          const isSelected = selectedRowIds?.includes(row.id);
          const hasExpandedSibling =
            virtualIndex + 1 < virtualItems.length &&
            virtualItems[virtualIndex + 1]?.type === 'expanded';

          return (
            <TableRow
              {...props}
              data-row-id={row.id}
              data-state={(row.getIsSelected() || isSelected) && 'selected'}
              className={cn(
                'border-b-0 [&>td]:bg-surface-base',
                ((isLastDataRow && !showLastBorderBottom) || hasExpandedSibling) &&
                  '[&>td]:border-b-0',
                // border-separate モードでは td に bg-surface-base が直接当たるため、選択状態は td 側に当てる
                isSelected && '[&>td]:bg-surface-accent',
                // border-separate モードでは box-shadow (inset) が td 単位に分断されるため、
                // outline でハイライト枠を表現する（通常テーブルの shadow-[inset...] とは手法が異なる）
                isHighlighted && 'outline-2 outline-[#059669] outline-solid -outline-offset-2',
              )}
            >
              {children}
            </TableRow>
          );
        },
      }}
      fixedHeaderContent={() => <>{renderHeaderContent()}</>}
      itemContent={(virtualIndex) => {
        const item = virtualItems[virtualIndex];
        if (!item) return null;

        const row = rows[item.rowIndex];
        if (!row) return null;

        if (item.type === 'expanded') {
          const expandedContent = expandedRowRender?.(row);
          return (
            <TableCell colSpan={columns.length} className="p-0">
              {expandedContent}
            </TableCell>
          );
        }

        const isHighlighted = highlightedRowIds?.includes(row.id);
        const visibleCells = row.getVisibleCells();

        return (
          <>
            {visibleCells.map((cell, cellIndex) => {
              const style = columnStyles[cell.column.id];
              const weightedStyle = weightedLeafWidthStyles[cell.column.id];
              const mergedStyle = mergeColumnStyles(style, weightedStyle);
              const pinned = pinnedColumnStyles?.[cell.column.id];
              const finalStyle = pinned ? { ...mergedStyle, ...pinned.style } : mergedStyle;
              const pinnedDataAttrs = pinned
                ? pinned.side === 'left'
                  ? { 'data-pinned-left': pinned.pinnedOffset }
                  : { 'data-pinned-right': pinned.pinnedOffset }
                : {};
              return (
                <TableCell
                  key={cell.id}
                  style={finalStyle}
                  className={cn(
                    getPinnedClassName({ pinned, isCell: true }),
                    isHighlighted &&
                      pinned &&
                      getHighlightedPinnedClassName({
                        isFirstCell: cellIndex === 0,
                        isLastCell: cellIndex === visibleCells.length - 1,
                      }),
                  )}
                  {...pinnedDataAttrs}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              );
            })}
          </>
        );
      }}
    />
  );
};
